import { Form, Formik } from 'formik';
import { Copy, X } from 'lucide-react';
import { object, string } from 'yup';
import { useCurrentStateAndParams } from '@uirouter/react';

import { Button } from '@@/buttons';
import { Modal } from '@@/modals';
import { ModalHeader } from '@@/modals/Modal/ModalHeader';
import { ModalBody } from '@@/modals/Modal/ModalBody';
import { ModalFooter } from '@@/modals/Modal/ModalFooter';
import { Input } from '@@/form-components/Input';
import { FormControl } from '@@/form-components/FormControl';
import { LoadingButton } from '@@/buttons/LoadingButton';

import { useCloneTag } from '../../queries/useCloneTag';

const schema = object().shape({
  targetTag: string()
    .required('Tag name is required')
    .matches(
      /^[a-zA-Z0-9][a-zA-Z0-9_.-]*$/,
      'Tag can only contain alphanumeric (a-zA-Z0-9) and special _ . - characters. Tag must not start with . - characters.'
    ),
});

interface CloneTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  sourceTag: string;
}

export function CloneTagModal({
  isOpen,
  onClose,
  sourceTag,
}: CloneTagModalProps) {
  const {
    params: { id: registryId, repository: repositoryName, endpointId },
  } = useCurrentStateAndParams();

  const cloneTagMutation = useCloneTag();

  function handleSubmit({ targetTag }: { targetTag: string }) {
    cloneTagMutation.mutate(
      {
        registryId: Number(registryId),
        repository: repositoryName,
        sourceTag,
        targetTag,
        environmentId: endpointId ? Number(endpointId) : undefined,
      },
      {
        onSuccess: () => {
          // Close the modal after successfully cloning the tag
          onClose();
        },
      }
    );
  }

  function handleClose() {
    if (!cloneTagMutation.isLoading) {
      onClose();
    }
  }

  if (!isOpen) {
    return null;
  }

  return (
    <Modal onDismiss={handleClose} aria-label="Clone tag">
      <ModalHeader title="Clone Tag" />
      <Formik
        initialValues={{ targetTag: '' }}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, setFieldValue }) => (
          <Form>
            <ModalBody>
              <div className="form-group">
                <div className="small text-muted">
                  This will create a new tag pointing to the same image manifest
                  as the source tag. The source tag will remain unchanged.
                </div>
              </div>
              <div className="form-group">
                <FormControl
                  label="Source tag name"
                  inputId="sourceTag"
                  size="vertical"
                >
                  <Input
                    id="sourceTag"
                    type="text"
                    value={sourceTag}
                    readOnly
                    className="form-control"
                    data-cy="source-tag-input"
                  />
                </FormControl>
              </div>

              <div className="form-group">
                <FormControl
                  label="Target tag name"
                  inputId="targetTag"
                  required
                  size="vertical"
                  errors={errors.targetTag}
                >
                  <Input
                    id="targetTag"
                    type="text"
                    value={values.targetTag}
                    onChange={(e) => setFieldValue('targetTag', e.target.value)}
                    placeholder="Enter new tag name"
                    className="form-control"
                    // the tag name is the only field of the modal, focus it on open
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    data-cy="clone-tag-input"
                  />
                </FormControl>
              </div>
            </ModalBody>

            <ModalFooter>
              <Button
                type="button"
                color="default"
                onClick={handleClose}
                disabled={cloneTagMutation.isLoading}
                icon={X}
                data-cy="clone-tag-cancel"
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                color="primary"
                disabled={cloneTagMutation.isLoading}
                isLoading={cloneTagMutation.isLoading}
                loadingText="Cloning..."
                icon={Copy}
                data-cy="clone-tag-submit"
              >
                Clone Tag
              </LoadingButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
